/*
 * A/B test de thème (Intelligems) : faire remonter sur la commande le thème
 * sur lequel le client a acheté.
 *
 * Deux canaux, écrits l'un après l'autre (voir syncWithToken) :
 * 1. Attributs de panier (/cart/update.js) — méthode éprouvée du thème, visibles
 *    dans les « attributs supplémentaires » de la commande.
 * 2. Métachamp de panier custom.ab_test_theme (Storefront API cartMetafieldsSet),
 *    recopié automatiquement dans le métachamp de commande de même namespace/clé
 *    quand la définition de commande a la capacité cartToOrderCopyable.
 *
 * Le thème est identifié par un libellé posé en réglage (A / B) et non par
 * l'objet Liquid `theme`, déprécié par Shopify. Voir docs/AB-TEST-theme-commande.md.
 */
(function () {
  const config = window.greezAbThemeConfig || {};
  if (!config.enabled) return;

  const themeLabel = String(config.themeLabel || "").trim();
  if (!themeLabel) return;

  // CartMetafieldsSetInput n'a pas de champ `namespace` : il se passe dans la
  // clé composite « namespace.clé ».
  const metafieldKey = "custom.ab_test_theme";
  const metafieldMaxRetries = 2;
  const metafieldRetryDelayMs = 1500;
  const syncedStorageKey = "greez_ab_theme_synced";
  const intelligemsTimeoutMs = 4000;
  let lastCartToken = "";

  function readSyncedSignature() {
    try {
      return window.sessionStorage.getItem(syncedStorageKey) || "";
    } catch (error) {
      return "";
    }
  }

  function writeSyncedSignature(signature) {
    try {
      window.sessionStorage.setItem(syncedStorageKey, signature);
    } catch (error) {
      return;
    }
  }

  /*
   * Groupe Intelligems du visiteur. Avec un ID d'expérience en réglage, on lit ce
   * test précisément ; sinon on prend le premier test actif où il est affecté.
   */
  function getIntelligemsDetails() {
    const user = window.igData && window.igData.user;
    const empty = { experimentId: "", groupName: "", groupId: "" };
    if (!user || typeof user.getTestGroup !== "function") return empty;

    try {
      const experimentIds = config.experimentId
        ? [config.experimentId]
        : (typeof user.getExperiments === "function" ? user.getExperiments() || [] : []).map(function (experiment) {
            return experiment.id;
          });

      for (const experimentId of experimentIds) {
        const group = user.getTestGroup(experimentId);
        if (group) {
          return {
            experimentId: String(experimentId || ""),
            groupName: String(group.name || ""),
            groupId: String(group.id || ""),
          };
        }
      }
    } catch (error) {
      return empty;
    }

    return empty;
  }

  /*
   * Intelligems pose window.igData une fois chargé et émet `ig:ready`. Le délai
   * de secours garantit que le libellé de thème est écrit même si l'app ne charge
   * pas (bloqueur, erreur) : le groupe de test sera alors simplement vide.
   */
  function whenIntelligemsReady(callback) {
    let done = false;
    function finish() {
      if (done) return;
      done = true;
      callback();
    }

    if (window.igData && window.igData.user) {
      finish();
      return;
    }

    window.addEventListener("ig:ready", finish, { once: true });
    window.setTimeout(finish, intelligemsTimeoutMs);
  }

  function fetchCartToken() {
    return window
      .fetch(config.cartUrl || "/cart.js", { credentials: "same-origin" })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (cart) {
        return cart && cart.token ? String(cart.token) : "";
      })
      .catch(function () {
        return "";
      });
  }

  function writeCartAttributes(details, keepalive) {
    if (!config.cartUpdateUrl) return Promise.resolve(false);

    const payload = {
      attributes: {
        "__ab_theme": themeLabel,
        "__ab_test_experiment": details.experimentId,
        "__ab_test_group": details.groupName,
        "__ab_test_group_id": details.groupId,
      },
    };

    return window
      .fetch(config.cartUpdateUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "same-origin",
        keepalive: keepalive,
      })
      .then(function (response) {
        return response.ok;
      })
      .catch(function () {
        return false;
      });
  }

  /*
   * Le `token` de /cart.js inclut déjà son suffixe `?key=` : l'ID de panier
   * Storefront API complet est donc `gid://shopify/Cart/<token>`.
   */
  function writeCartMetafield(cartToken, keepalive) {
    const headers = { "Content-Type": "application/json" };
    if (config.storefrontToken) {
      headers["X-Shopify-Storefront-Access-Token"] = config.storefrontToken;
    }

    const body = {
      query:
        "mutation greezAbTheme($metafields: [CartMetafieldsSetInput!]!) {" +
        " cartMetafieldsSet(metafields: $metafields) {" +
        " userErrors { code field message }" +
        " } }",
      variables: {
        metafields: [
          {
            ownerId: "gid://shopify/Cart/" + cartToken,
            key: metafieldKey,
            type: "single_line_text_field",
            value: themeLabel,
          },
        ],
      },
    };

    return window
      .fetch("/api/" + (config.storefrontApiVersion || "2026-07") + "/graphql.json", {
        method: "POST",
        headers: headers,
        body: JSON.stringify(body),
        keepalive: keepalive,
      })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      // Seules les erreurs sont demandées : relire le métachamp exigerait l'accès
      // unauthenticated_read_metafields, inutile pour l'écrire.
      .then(function (result) {
        const payload = result && result.data && result.data.cartMetafieldsSet;
        const errors = ((result && result.errors) || []).concat((payload && payload.userErrors) || []);
        return Boolean(payload && !errors.length);
      })
      .catch(function () {
        return false;
      });
  }

  function syncWithToken(cartToken, keepalive) {
    if (!cartToken) return Promise.resolve(false);

    const details = getIntelligemsDetails();
    const signature = [cartToken, themeLabel, details.groupId].join("|");
    if (readSyncedSignature() === signature) return Promise.resolve(true);

    // Attributs PUIS métachamp, jamais en parallèle : un /cart/update.js
    // simultané efface le métachamp sans renvoyer d'erreur (constaté en prod).
    return writeCartAttributes(details, keepalive)
      .then(function () {
        return writeVerifiedCartMetafield(cartToken, keepalive, 0);
      })
      .then(function (written) {
        // Signature mémorisée seulement si le métachamp est posé : sinon la page
        // suivante retente.
        if (written) writeSyncedSignature(signature);
        return written;
      });
  }

  /*
   * Relit le métachamp après écriture et retente s'il a été écrasé, par exemple
   * par une autre écriture d'attributs du thème au même moment (attribution
   * UTM). Sans droit de lecture, la valeur est illisible (`undefined`) : on se
   * fie alors à l'absence d'erreur. Pas de relecture au départ vers le checkout.
   */
  function writeVerifiedCartMetafield(cartToken, keepalive, attempt) {
    return writeCartMetafield(cartToken, keepalive).then(function (written) {
      if (!written || keepalive) return written;
      return readCartMetafield(cartToken).then(function (value) {
        if (value === undefined || value === themeLabel) return true;
        if (attempt >= metafieldMaxRetries) return false;
        return new Promise(function (resolve) {
          window.setTimeout(resolve, metafieldRetryDelayMs);
        }).then(function () {
          return writeVerifiedCartMetafield(cartToken, keepalive, attempt + 1);
        });
      });
    });
  }

  /* Valeur actuelle du métachamp de panier, `undefined` si illisible. */
  function readCartMetafield(cartToken) {
    const headers = { "Content-Type": "application/json" };
    if (config.storefrontToken) {
      headers["X-Shopify-Storefront-Access-Token"] = config.storefrontToken;
    }

    const parts = metafieldKey.split(".");
    const body = {
      query:
        "query greezAbThemeRead($id: ID!, $namespace: String!, $key: String!) {" +
        " cart(id: $id) { metafield(namespace: $namespace, key: $key) { value } } }",
      variables: { id: "gid://shopify/Cart/" + cartToken, namespace: parts[0], key: parts[1] },
    };

    return window
      .fetch("/api/" + (config.storefrontApiVersion || "2026-07") + "/graphql.json", {
        method: "POST",
        headers: headers,
        body: JSON.stringify(body),
      })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (result) {
        if (!result || (result.errors && result.errors.length) || !result.data || !result.data.cart) return undefined;
        const metafield = result.data.cart.metafield;
        return metafield ? String(metafield.value) : "";
      })
      .catch(function () {
        return undefined;
      });
  }

  /*
   * Synchronisation complète : relit le panier (un panier vidé ou expiré change
   * de token, la signature force alors une réécriture).
   */
  function sync() {
    return fetchCartToken().then(function (cartToken) {
      lastCartToken = cartToken;
      return syncWithToken(cartToken, false);
    });
  }

  /*
   * Filet de sécurité non bloquant au départ vers le checkout : réécrit avec
   * `keepalive` pour que la requête survive à la navigation. On réutilise le
   * token connu, sans aller-retour /cart.js qui risquerait d'arriver trop tard.
   */
  document.addEventListener(
    "click",
    function (event) {
      const target = event.target.closest('[name="checkout"], a[href*="/checkout"]');
      if (!target || !lastCartToken) return;
      syncWithToken(lastCartToken, true);
    },
    true
  );

  window.GreezAbTheme = {
    sync: sync,
    getThemeLabel: function () {
      return themeLabel;
    },
  };

  whenIntelligemsReady(sync);
})();
